import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { consumeRedirectPath, markCallbackPending, clearCallbackPending } from './Auth/RequireAuth'
import api from '../services/api'
import UsernameModal from './UsernameModal'
import HandleSelectionModal from './HandleSelection/HandleSelectionModal'

function AuthCallback() {
  const { checkAuth } = useAuth()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [showUsernameModal, setShowUsernameModal] = useState(false)
  const [showHandleModal, setShowHandleModal] = useState(false)
  const [proposedHandle, setProposedHandle] = useState(null)
  const [userData, setUserData] = useState(null)

  // Where the user was headed before being bounced to login (M14). Falls back
  // to the shelter hub when nothing was stored.
  const redirectTarget = () => {
    const path = consumeRedirectPath() || '/shelter'
    clearCallbackPending()
    return path
  }

  useEffect(() => {
    // Block PublicLayout's authenticated-redirect effect from consuming the
    // stored destination while this callback resolves (M14 race).
    markCallbackPending()
    const token = searchParams.get('token')
    console.log('AuthCallback: Token received', token ? 'Yes' : 'No')
    if (token) {
      localStorage.setItem('access_token', token)
      console.log('AuthCallback: Token stored, verifying with /auth/me...')
      // Verify the token works before calling onAuthSuccess
      api.get('/auth/me')
        .then((response) => {
          console.log('AuthCallback: Token verified successfully', response.data)
          setUserData(response.data)

          // Check if handle is set - need to get player state
          api.get('/player/state')
            .then(async (playerRes) => {
              console.log('AuthCallback: Player state received', playerRes.data)

              // Priority 1: Check if handle needs to be set
              if (!playerRes.data.handle) {
                // Check if we got handle_conflict from player creation
                console.log('AuthCallback: Player has no handle, showing handle selection modal')
                setShowHandleModal(true)
                // Try to get proposed handle from localStorage if available
                const proposed = localStorage.getItem('proposed_handle')
                if (proposed) {
                  setProposedHandle(proposed)
                  localStorage.removeItem('proposed_handle')
                }
              } else if (!response.data.username) {
                // Priority 2: Check if username is set
                console.log('AuthCallback: Player has handle, checking username')
                setShowUsernameModal(true)
              } else {
                // All set, proceed to game
                console.log('AuthCallback: All required fields set, updating context and navigating')
                await checkAuth()
                navigate(redirectTarget())
              }
            })
            .catch(async (error) => {
              console.error('AuthCallback: Failed to get player state:', error)
              // If player state fails, check username as fallback
              if (!response.data.username) {
                setShowUsernameModal(true)
              } else {
                await checkAuth()
                navigate(redirectTarget())
              }
            })
        })
        .catch((error) => {
          console.error('AuthCallback: Failed to verify token:', {
            message: error.response?.data?.detail || error.message || 'Unknown error',
            status: error.response?.status,
            statusText: error.response?.statusText,
            error: error
          })
          localStorage.removeItem('access_token')
          clearCallbackPending()
          navigate('/')
        })
    } else {
      console.error('AuthCallback: No token received in callback')
      clearCallbackPending()
      navigate('/')
    }
  }, [searchParams, navigate, checkAuth])

  const handleHandleSet = async (newHandle) => {
    setShowHandleModal(false)
    // After setting handle, check if username is needed
    if (!userData?.username) {
      setShowUsernameModal(true)
    } else {
      await checkAuth()
      navigate(redirectTarget())
    }
  }

  const handleUsernameSet = async () => {
    setShowUsernameModal(false)
    await checkAuth()
    navigate(redirectTarget())
  }

  if (showHandleModal) {
    return <HandleSelectionModal proposedHandle={proposedHandle} onHandleSet={handleHandleSet} />
  }

  if (showUsernameModal) {
    return <UsernameModal onUsernameSet={handleUsernameSet} />
  }

  return <div className="text-text-lighter p-4 text-center">Completing login...</div>
}

export default AuthCallback

